import { DestroyRef, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { IncidentsDataService } from '@services/data/incidents-data.service';
import { UserStateService } from '@services/user-state/user-state.service';
import { ToastService } from '@ndwnu/design-system';
import { trafficPath } from '@daisi/app.paths';

@Injectable({
  providedIn: 'root',
})
export class QueryParamService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly imDataService = inject(IncidentsDataService);
  private readonly userStateService = inject(UserStateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  readonly selectIncidentIfPossible: WritableSignal<string | number | undefined> =
    signal(undefined);
  currentQueryParamValue: string | undefined = undefined;

  pageLoaded = false;

  constructor() {
    // Select or deselect the incident from the query param when it changes
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const selectedIncidentId = params['incidentId'];
      this.currentQueryParamValue = selectedIncidentId;

      if (!selectedIncidentId) {
        this.userStateService.state.detailPanelOpen.set(false);
      } else if (selectedIncidentId !== this.userStateService.state.openIncident()?.properties.id) {
        this.selectIncidentIfPossible.set(selectedIncidentId);
      }

      this.handleClosureQueryParam(params['closureId']);
    });

    // Make sure the feature from the query param is selected as soon as it is available
    effect((onCleanup) => {
      var allIncidents = this.imDataService.allFeatures();

      let current = this.userStateService.state.openIncident()?.properties.id;
      let queryParamSelectedIncidentId = this.selectIncidentIfPossible();
      if (!queryParamSelectedIncidentId) {
        // no query param selected feature id, nothing to do
        return;
      }

      if (current === queryParamSelectedIncidentId) {
        // it is already selected, clear the query param field
        this.selectIncidentIfPossible.set(undefined);
        return;
      }

      // find the feature with the query param id
      let feature = allIncidents.find((f) => f.properties.id === queryParamSelectedIncidentId);
      if (feature) {
        // Found it!
        this.userStateService.state.selectedIncident.set(feature);
        this.userStateService.state.detailPanelOpen.set(true);
        this.selectIncidentIfPossible.set(undefined);
      } else {
        // Not found, now we will wait for 1500 ms for the feature to become available
        const timeoutId = setTimeout(() => {
          this.toastService.open(`Het incident is niet meer beschikbaar.`);
          this.router.navigate([], { relativeTo: this.route });
          this.selectIncidentIfPossible.set(undefined);
        }, 1000);

        // If this effect is run again because a signal was updated, stop the timeout
        onCleanup(() => clearTimeout(timeoutId));
      }
    });

    // Keep the query param in sync with the selected feature
    effect(() => {
      let current = this.userStateService.state.openIncident()?.properties.id;

      // The first time current is always undefined
      if (!this.pageLoaded) {
        this.pageLoaded = true;
        return;
      }

      if (!current) {
        this.router.navigate([], { relativeTo: this.route });
      } else if (current !== this.currentQueryParamValue) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { incidentId: current || null },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  private handleClosureQueryParam(closureId: string | undefined) {
    this.userStateService.state.deepLinkClosureId.set(closureId || undefined);

    if (closureId && !this.router.url.split('?')[0].endsWith(trafficPath)) {
      this.router.navigate([trafficPath], {
        queryParams: { closureId },
        queryParamsHandling: 'merge',
      });
    }
  }
}
